import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import usePageTitle from "@/hooks/use-page-title";
import { Image, Trash2, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  { value: "founder_aakash", label: "Founder — Aakash", single: true },
  { value: "founder_aashish", label: "Founder — Aashish", single: true },
  { value: "product", label: "Product Photos", single: false, hint: "Photos of jobs delivered (banners, sign boards, cards etc.) shown on /products" },
  { value: "portfolio", label: "Portfolio Photos", single: false, hint: "Featured showcase work displayed on /portfolio" },
  { value: "client", label: "Client Logos", single: false, hint: "Uploaded logos will slide across the Home page 'Our Clients' section" },
];

export default function Media() {
  usePageTitle("Media");
  const [rows, setRows] = useState([]);
  const [activeCat, setActiveCat] = useState("founder_aakash");
  const [label, setLabel] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/media").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/media?category=${activeCat}&label=${encodeURIComponent(label)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLabel("");
      e.target.value = "";
      load();
    } catch (e2) {
      alert(formatApiError(e2));
    } finally { setUploading(false); }
  };

  const remove = async (m) => {
    if (!window.confirm("Delete this photo?")) return;
    try { await api.delete(`/media/${m.id}`); load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  const inCat = (cat) => rows.filter((r) => r.category === cat);

  return (
    <div className="space-y-6" data-testid="media-root">
      <div className="border-b border-[hsl(var(--border))] pb-6 flex items-end justify-between">
        <div>
          <div className="overline mb-2">Website Media</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Photos</h1>
          <p className="text-sm text-muted-foreground mt-2">Upload founder portraits, product photos and portfolio images that appear on the public website.</p>
        </div>
        <Image size={40} className="text-[#FF6B00]" />
      </div>

      <div className="card-swiss p-5 space-y-4">
        <div className="overline">Upload New Photo</div>
        <div className="grid md:grid-cols-4 gap-3">
          <select
            value={activeCat}
            onChange={(e) => setActiveCat(e.target.value)}
            className="border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
            data-testid="media-category-select"
          >
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Caption / label (optional)"
            className="border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00] md:col-span-2"
            data-testid="media-label-input"
          />
          <label className="btn-swiss btn-accent cursor-pointer justify-center" data-testid="media-upload-label">
            <Upload size={14} /> {uploading ? "Uploading…" : "Choose Photo"}
            <input type="file" accept="image/*" onChange={upload} className="hidden" disabled={uploading} data-testid="media-upload-input" />
          </label>
        </div>
        <div className="text-xs text-muted-foreground">JPEG, JPG, PNG, WEBP · max 5 MB · founders replace the existing photo automatically.</div>
      </div>

      {CATEGORIES.map((c) => (
        <div key={c.value} className="card-swiss p-5" data-testid={`media-section-${c.value}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="overline">{c.label} ({inCat(c.value).length})</div>
            {c.hint && <div className="text-xs text-muted-foreground max-w-md text-right">{c.hint}</div>}
          </div>
          {inCat(c.value).length === 0 ? (
            <div className="text-sm text-muted-foreground py-6">No photos uploaded yet.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {inCat(c.value).map((m) => (
                <div key={m.id} className="relative aspect-square border border-[hsl(var(--border))] overflow-hidden group bg-[hsl(var(--muted))]" data-testid={`media-${m.id}`}>
                  <img src={`${API}/media/${m.id}`} alt={m.label} className={`w-full h-full ${c.value === "client" ? "object-contain p-3" : "object-cover"}`} />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                    <div className="text-[10px] text-white truncate">{m.label || "—"}</div>
                    <button onClick={() => remove(m)} className="ml-auto bg-white text-[#FF6B00] p-1.5" data-testid={`delete-media-${m.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
